import type { ReactNode } from 'react';
import { Icono } from './Icono';
import type { TipoBloqueIA } from '../../modelo/tipos';
import { useRuta } from '../../lib/ruta';

/** Botón-píldora seleccionable (filtros, emociones, tipos). */
export function Chip({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button className={activo ? 'chip on' : 'chip'} aria-pressed={activo} onClick={onClick}>
      {children}
    </button>
  );
}

/** Grupo de chips de selección única o múltiple. */
export function GrupoChips({
  etiqueta, opciones, seleccion, onElegir, desplazable
}: {
  etiqueta: string;
  opciones: string[];
  seleccion: string | string[];
  onElegir: (opcion: string) => void;
  desplazable?: boolean;
}) {
  const activa = (o: string) => (Array.isArray(seleccion) ? seleccion.includes(o) : seleccion === o);
  return (
    <div className={desplazable ? 'chips scroll' : 'chips'} role="group" aria-label={etiqueta}>
      {opciones.map((o) => (
        <Chip key={o} activo={activa(o)} onClick={() => onElegir(o)}>{o}</Chip>
      ))}
    </div>
  );
}

/** Etiqueta que marca una función simulada o solo visual. */
export function Demo({ children = 'Demo' }: { children?: ReactNode }) {
  return <span className="demo">{children}</span>;
}

/** Encabezado de pantalla: antetítulo, título y subtítulo opcionales, y una acción a la derecha. */
export function Cabecera({ antetitulo, titulo, subtitulo, accion }: { antetitulo: string; titulo: string; subtitulo?: string; accion?: ReactNode }) {
  const cuerpo = (
    <div>
      <p className="eyebrow">{antetitulo}</p>
      <h1 className="display">{titulo}</h1>
      {subtitulo ? <p className="muted" style={{ margin: '6px 0 0' }}>{subtitulo}</p> : null}
    </div>
  );
  return accion ? <header className="between">{cuerpo}{accion}</header> : <header>{cuerpo}</header>;
}

/** Botón «volver» a una pantalla anterior. */
export function Volver({ a, children }: { a: string; children: ReactNode }) {
  const { ir } = useRuta();
  return (
    <div className="topbar">
      <button className="btn quiet" onClick={() => ir(a)}><Icono nombre="back" />{children}</button>
    </div>
  );
}

/** Bloque de respuesta de IA con su naturaleza explícita: cita, interpretación o datos que faltan. */
export function BloqueIA({ tipo, etiqueta, children, pequeno }: { tipo: TipoBloqueIA; etiqueta: string; children: ReactNode; pequeno?: boolean }) {
  return (
    <div className={'ai-block ' + tipo}>
      <span className="eyebrow">{etiqueta}</span>
      <p className={pequeno ? 'small' : undefined} style={{ margin: '4px 0 0' }}>{children}</p>
    </div>
  );
}

/** Caja con candado para contenido sin permiso de IA. */
export function Bloqueado({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="locked-box row" style={{ alignItems: 'flex-start' }}>
      <Icono nombre="lock" />
      <div>
        <p style={{ margin: 0, fontWeight: 600 }}>{titulo}</p>
        <p className="small muted" style={{ margin: '4px 0 0' }}>{children}</p>
      </div>
    </div>
  );
}

export function Pensando({ children }: { children: ReactNode }) {
  return <p className="breathe muted" style={{ margin: 0 }}>{children}</p>;
}

export function TituloSeccion({ children, id }: { children: ReactNode; id?: string }) {
  return <h2 id={id} className="display" style={{ fontSize: 22, margin: 0 }}>{children}</h2>;
}
